import { createAsyncThunk } from '@reduxjs/toolkit';

import { projectsApi } from '@/lib/apis';
import type { RootState } from '@/stores/store';

import { JSONProjectFile } from '../../../custom-shared/types/project';
import {
  selectIsLocalProject,
  selectProject,
  selectProjectDefinitionFileByName,
  selectProjectFileByName,
} from '../project/project.selectors';

import { selectSyncingFilesAsArray } from './sync.selectors';
import { SyncFileDescription } from './types';

export const loadApplySync = createAsyncThunk<SyncFileDescription[], void>(
  'sync/apply',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const project = selectProject(state);
    const isLocalProject = selectIsLocalProject(state);
    const preparedFiles = selectSyncingFilesAsArray(state);

    if (!preparedFiles.length || !project || isLocalProject) {
      return [];
    }

    for (const pendingFile of preparedFiles) {
      const { type, baseName } = pendingFile;

      let updatedFile: JSONProjectFile | null = null;

      if (type === 'instance') {
        const projectFile = selectProjectFileByName(state, baseName);
        updatedFile = projectFile?.json || null;
      } else {
        updatedFile = selectProjectDefinitionFileByName(state, baseName);
      }

      if (!updatedFile || !updatedFile.content) {
        continue;
      }

      const fullName = getFileFullName(pendingFile);
      const file = new File([JSON.stringify(updatedFile.content)], fullName, {
        type: 'application/json',
      });

      // it may throw: see middlewares
      await projectsApi.projectsFilesPut(
        project.id,
        getFileFullName(pendingFile),
        file,
      );
    }

    return preparedFiles;
  },
);

function getFileFullName(pending: SyncFileDescription) {
  if (pending.type === 'instance') {
    return `${pending.baseName}.json`;
  } else {
    return `${pending.baseName}.definition.json`;
  }
}
